/**
 * Cálculos puros do Cadastro de Suprimentos (redesign 2026-09-15).
 *
 * Só o que é APRESENTAÇÃO fica aqui: barra saldo × mínimo, cobertura em
 * dias, valor em estoque, agrupamento por tipo, ordem por criticidade,
 * amostra de cor e formatação. O que é regra de negócio (último preço,
 * consumo, sugestão de compra, saldo após cada movimento, onde é usado)
 * vem do backend (ADR-0070) — não recalcular aqui, senão viram duas
 * verdades.
 */
import { formatarQuantidade, formatarReaisBR } from "../../../utils/dinheiro";

const num = (v) => (v === null || v === undefined || v === "" ? 0 : Number(v));

export function qtd(v) {
  return v === null || v === undefined || v === "" ? "—" : formatarQuantidade(v);
}

// ---------------------------------------------------------------- saldo

/** Cadastro ativo sem custo médio: entra no orçamento a R$ 0 (ADR-0068). */
export function semPreco(p) {
  return Boolean(p.ativo) && !(num(p.custo_medio) > 0);
}

/** Mais de 2× o mínimo (com mínimo definido) = dinheiro parado na prateleira. */
export function temExcesso(p) {
  const min = num(p.estoque_minimo);
  return min > 0 && num(p.saldo) > min * 2;
}

/** Tem saldo e nada saiu em 90 dias (consumo servido pelo backend). */
export function semGiro(p) {
  return num(p.saldo) > 0 && p.consumo_90d !== undefined && num(p.consumo_90d) === 0;
}

export function semFornecedor(p) {
  return num(p.fornecedores_ativos) === 0;
}

export function valorEstoque(p) {
  return num(p.saldo) * num(p.custo_medio);
}

/** Abaixo de 130% do mínimo a barra fica laranja: ainda dá, mas está no limite. */
const LIMIAR_ATENCAO_PCT = 130;

/**
 * Saldo × mínimo para a barra da linha e os mini-cards do detalhe.
 * `pct` é limitado a 100 (barra cheia = no mínimo ou acima).
 */
export function infoSaldo(p) {
  const saldo = num(p.saldo);
  const min = num(p.estoque_minimo);
  const reservado = num(p.reservado);
  const disponivel = p.disponivel === undefined ? saldo - reservado : num(p.disponivel);
  const abaixo = Boolean(p.alerta_minimo) || (min > 0 && saldo < min);
  const razao = min > 0 ? (saldo / min) * 100 : (saldo > 0 ? 100 : 0);
  const pct = Math.max(0, Math.min(100, Math.round(razao)));

  let tone = "success";
  if (saldo <= 0) tone = "neutral";
  else if (abaixo) tone = "danger";
  else if (min > 0 && razao < LIMIAR_ATENCAO_PCT) tone = "warning";

  let sub = "sem reservas";
  if (saldo <= 0) sub = "sem saldo";
  else if (reservado > 0) sub = `res. ${qtd(reservado)} · disp. ${qtd(disponivel)}`;

  return { saldo, min, reservado, disponivel, abaixo, pct, tone, sub };
}

/** Quantos dias o saldo aguenta no ritmo dos últimos 30 (null = sem consumo). */
export function coberturaDias(saldo, consumo30) {
  if (!(num(consumo30) > 0)) return null;
  return Math.max(0, Math.round((num(saldo) / num(consumo30)) * 30));
}

/** Último preço servido na linha, ou null quando nenhum fornecedor tem preço. */
export function ultimoPrecoDe(p) {
  if (p.ultimo_preco === null || p.ultimo_preco === undefined) return null;
  return { valor: num(p.ultimo_preco), fornecedor: p.ultimo_preco_fornecedor || "", data: p.ultimo_preco_em || null };
}

// ------------------------------------------------------------- agrupamento

export const SEM_TIPO = { codigo: "", label: "Sem tipo", familia: "—" };

/**
 * Agrupa pelo tipo de suprimento, na ordem do catálogo (família, depois
 * tipo); itens sem tipo vão para o fim. Cada grupo já traz o que o
 * cabeçalho mostra: quantos abaixo do mínimo e o valor em estoque.
 */
export function agruparPorTipo(produtos, tipos = []) {
  const ordem = new Map(tipos.map((t, i) => [t.codigo, i]));
  const grupos = new Map();
  produtos.forEach((p) => {
    const codigo = p.tipo_suprimento || "";
    if (!grupos.has(codigo)) {
      const t = tipos.find((x) => x.codigo === codigo) || (codigo ? { codigo, label: codigo, familia: "—" } : SEM_TIPO);
      grupos.set(codigo, { key: codigo || "__sem_tipo", codigo, label: t.label, familia: t.familia, itens: [], abaixo: 0, valor: 0 });
    }
    const g = grupos.get(codigo);
    g.itens.push(p);
    if (p.alerta_minimo) g.abaixo += 1;
    g.valor += valorEstoque(p);
  });
  return [...grupos.values()].sort((a, b) => {
    const ia = a.codigo ? ordem.get(a.codigo) ?? 9998 : 9999;
    const ib = b.codigo ? ordem.get(b.codigo) ?? 9998 : 9999;
    return ia - ib;
  });
}

// ------------------------------------------------------------- ordenação

const PESO_TONE = { danger: 0, warning: 1, neutral: 2, success: 3 };

/** Abaixo do mínimo primeiro, depois no limite, depois o resto; empate por nome. */
export function ordenarPorCriticidade(produtos) {
  return [...produtos].sort((a, b) => {
    const ta = PESO_TONE[infoSaldo(a).tone];
    const tb = PESO_TONE[infoSaldo(b).tone];
    if (ta !== tb) return ta - tb;
    return String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", { numeric: true });
  });
}

// ------------------------------------------------------------------- cor

/**
 * Amostra de cor da linha. Fonte: `atributos.cor_hex` (campo do tipo, ADR-0070).
 * Sem ele, deriva de nomes comuns de padrão da marcenaria — só fallback
 * visual, até alguém preencher a amostra no cadastro; nome desconhecido
 * devolve null e a tela mostra o glifo.
 */
const CORES_CONHECIDAS = [
  ["branco", "#F2F1EC"], ["off white", "#EDE9E0"], ["preto", "#1F2124"], ["grafite", "#3B3F45"],
  ["cinza", "#9AA0A6"], ["areia", "#D6C7A8"], ["bege", "#D8C6A8"], ["blush", "#D9A5A0"], ["rosa", "#D9A5A0"],
  ["carvalho", "#B08A5B"], ["nogueira", "#6B4A2F"], ["freij", "#C9A575"], ["imbuia", "#5C3A21"],
  ["marrom", "#6E4B33"], ["madeira", "#A97B50"], ["azul", "#3A5A8C"], ["verde", "#4E7A5E"],
  ["vermelho", "#A93F3F"], ["amarelo", "#D9B54A"], ["aurum", "#C8A45C"], ["dourado", "#C8A45C"],
  ["cru", "#C8B48F"], ["natural", "#C8B48F"],
];

export function corDoItem(p) {
  const hex = (p.atributos || {}).cor_hex;
  if (typeof hex === "string" && /^#[0-9a-f]{6}$/i.test(hex)) return hex;
  const nome = String((p.atributos || {}).cor || "").toLowerCase();
  if (!nome) return null;
  const achada = CORES_CONHECIDAS.find(([chave]) => nome.includes(chave));
  return achada ? achada[1] : null;
}

// ------------------------------------------------------------- formatação

export function especificacaoDe(p, tipos = []) {
  const partes = [];
  const t = tipos.find((x) => x.codigo === p.tipo_suprimento);
  if (p.comprimento_mm || p.largura_mm || p.espessura_mm) {
    partes.push([p.comprimento_mm, p.largura_mm, p.espessura_mm].filter(Boolean).map((v) => formatarQuantidade(v)).join(" × ") + " mm");
  } else if ((p.atributos || {}).espessura_mm) {
    partes.push(`${formatarQuantidade(p.atributos.espessura_mm)} mm`);
  }
  if ((p.atributos || {}).largura_mm && !p.largura_mm) partes.push(`larg. ${formatarQuantidade(p.atributos.largura_mm)} mm`);
  if ((p.atributos || {}).metros_rolo) partes.push(`rolo ${formatarQuantidade(p.atributos.metros_rolo)} m`);
  if (p.fabricante && !partes.length) partes.push(p.fabricante);
  if (!partes.length && t?.label) partes.push(t.label);
  return partes.join(" · ") || "—";
}

export function moeda(v) {
  return v === null || v === undefined ? "—" : formatarReaisBR(v);
}

/**
 * Área BRUTA da chapa em m² (largura × comprimento), com 4 casas — ou null
 * sem as duas medidas. É o "m² por unidade" de produto em CH: o cadastro
 * calcula das medidas (2026-10-06), a mesma regra do backend
 * (`estoque.models.area_da_chapa_m2`, que grava o valor de verdade).
 */
export function areaDaChapaM2(larguraMm, comprimentoMm) {
  const l = Number(larguraMm);
  const c = Number(comprimentoMm);
  if (!(l > 0) || !(c > 0)) return null;
  return Math.round((l * c) / 100) / 10000;
}
